/**
 * Work-periods validation: checks date range, overlaps and per-period theoretical range.
 *
 * Note: keeps the same semantics as the original monolithic `validate` function.
 * Each work-period-level error is attached to both `errors.missionData.workPeriods`
 * (a single string for the summary) and `errors[`workPeriod-${isoDate}`]` (an array
 * to accumulate all errors for a given day).
 */
import { differenceInDays, isAfter, isBefore, parseISO } from 'date-fns'
import { isEqual } from 'lodash'
import { formatCompleteDate, formatISODate } from '../../../../../utils/date'
import { validateWorkPeriodTheoreticalRange } from '../../../../work-periods/validate-theoretical-range'
import {
  WorkersRequestCreateUpdateFormValues,
  WorkersRequestFormErrors,
} from '../workers-request-form.types'

/**
 * Validates work periods: dates consistency, 1-year cap, overlaps, missing start/end,
 * and per-period theoretical range (via `validateWorkPeriodTheoreticalRange`).
 *
 * @param values - Current Formik values.
 * @returns Partial errors object enriched with per-day error arrays.
 */
export const validateWorkPeriods = (
  values: WorkersRequestCreateUpdateFormValues,
): WorkersRequestFormErrors => {
  const errors: WorkersRequestFormErrors = {}

  const startDate = parseISO(values.meta.startDate)
  const endDate = parseISO(values.meta.endDate)

  // A mission must have at least one work period defined.
  if (!values.missionData.workPeriods || values.missionData.workPeriods.length === 0) {
    if (!errors.missionData) errors.missionData = {}
    errors.missionData.workPeriods = "Pas d'horaire de mission"
  }

  // Mission start cannot be after mission end.
  if (isAfter(startDate, endDate)) {
    if (errors.meta === undefined) errors.meta = {}
    errors.meta['startDate'] = 'La date de début doit être avant la date de fin'
  }

  // A mission cannot last more than 1 year.
  if (Math.abs(differenceInDays(startDate, endDate)) > 365) {
    if (errors.meta === undefined) errors.meta = {}
    errors.meta['endDate'] = 'Une mission doit durer maximum 1 an'
  }

  // Iterate one past the last index to allow comparing each period with the next one
  // without adding an extra guard branch (mirrors the original logic).
  for (let index = 0; index <= values.missionData.workPeriods.length; index++) {
    const workPeriod = values.missionData.workPeriods[index]
    const nextWorkPeriod = values.missionData.workPeriods[index + 1]
    const firstWorkPeriodStartDate =
      workPeriod && workPeriod.start ? workPeriod.start.date : undefined
    const firstWorkPeriodEndDate = workPeriod && workPeriod.end ? workPeriod.end.date : undefined
    const nextWorkPeriodStartDate =
      nextWorkPeriod && nextWorkPeriod.start ? nextWorkPeriod.start.date : undefined

    // End of a work period must strictly precede the start of the next one.
    if (firstWorkPeriodStartDate && firstWorkPeriodEndDate && nextWorkPeriodStartDate) {
      if (
        isBefore(nextWorkPeriodStartDate, firstWorkPeriodEndDate) ||
        isEqual(firstWorkPeriodEndDate, nextWorkPeriodStartDate)
      ) {
        const niceDate = formatCompleteDate(firstWorkPeriodStartDate)
        const isoDate = formatISODate(firstWorkPeriodStartDate)
        const error = `${niceDate} : L'heure de fin ne peut être plus grande ou égale à l'heure du début du jour suivant`
        if (!errors.missionData) errors.missionData = {}
        errors.missionData.workPeriods = error
        ;(errors[`workPeriod-${isoDate}`] ??= []).push(error)
      }
    }

    // A work period with an end date must also have a start date.
    if (firstWorkPeriodStartDate === undefined && firstWorkPeriodEndDate) {
      const date = firstWorkPeriodStartDate ?? firstWorkPeriodEndDate
      const niceDate = formatCompleteDate(date)
      const isoDate = formatISODate(date)
      const error = `${niceDate} : L'heure de début n'est pas précisée`
      if (!errors.missionData) errors.missionData = {}
      errors.missionData.workPeriods = error
      ;(errors[`workPeriod-${isoDate}`] ??= []).push(error)
    }

    // A work period with a start date must also have an end date.
    if (firstWorkPeriodStartDate && firstWorkPeriodEndDate === undefined) {
      const date = firstWorkPeriodStartDate ?? firstWorkPeriodEndDate
      const niceDate = formatCompleteDate(date)
      const isoDate = formatISODate(date!)
      const error = `${niceDate} : L'heure de fin n'est pas précisée`
      if (!errors.missionData) errors.missionData = {}
      errors.missionData.workPeriods = error
      ;(errors[`workPeriod-${isoDate}`] ??= []).push(error)
    }

    // Theoretical range: end after start, duration <= 24h.
    const rangeErrors =
      workPeriod &&
      validateWorkPeriodTheoreticalRange(workPeriod?.start?.date, workPeriod?.end?.date)
    if (rangeErrors && (rangeErrors.startDate || rangeErrors.endDate)) {
      const date = firstWorkPeriodStartDate ?? firstWorkPeriodEndDate
      const niceDate = date ? formatCompleteDate(date) : ''
      const isoDate = date ? formatISODate(date) : ''
      const error = `${niceDate} : ${rangeErrors.startDate ?? rangeErrors.endDate}`
      if (!errors.missionData) errors.missionData = {}
      errors.missionData.workPeriods = error
      if (isoDate) (errors[`workPeriod-${isoDate}`] ??= []).push(error)
    }
  }

  return errors
}
